"use client";

import React from "react";
import { Link } from "react-router-dom";
import overviewIcon from "../assets/images/trading/overview.svg";
import marketIcon from "../assets/images/trading/market.svg";
import buySellIcon from "../assets/images/trading/buySell.svg";
import sendIcon from "../assets/images/trading/sendReceive.svg";
import withdrawDepositIcon from "../assets/images/trading/widtdrawDeposit.svg";
import eightyEightPayIcon from "../assets/images/trading/88Pay.svg";
import payLinkIcon from "../assets/images/trading/paylink.svg";
import accountIcon from "../assets/images/trading/account.svg";
import { Zap, QrCode } from "lucide-react";
import { useFeatureFlagsByCategory } from "../hooks/query/queries";

const SvgIcon = ({ src, isActive }) => {
  const color = isActive ? "#33125F" : "#6D6584";
  const getFilter = (hexColor) => {
    if (hexColor === "#33125F") {
      return "brightness(0) saturate(100%) invert(20%) sepia(50%) saturate(2000%) hue-rotate(250deg) brightness(0.8) contrast(1.2)";
    }
    return "brightness(0) saturate(100%) invert(42%) sepia(8%) saturate(1234%) hue-rotate(218deg) brightness(95%) contrast(87%)";
  };

  return (
    <img
      src={src}
      alt=""
      className="w-[24px] h-[24px] flex items-center justify-center"
      style={{ filter: getFilter(color), display: "block" }}
    />
  );
};

function NavItemContent({ item, isActive }) {
  const IconComponent = item.IconComponent || Zap;

  return (
    <>
      {item.svg ? (
        <SvgIcon src={item.icon} isActive={isActive} />
      ) : (
        <IconComponent
          className="w-[24px] h-[24px]"
          stroke={isActive ? "#33125F" : "#6D6584"}
          fill="none"
        />
      )}
      <span className="text-[14px]">{item.label}</span>
    </>
  );
}

export default function BusinessSuiteSideNav({
  mainAccount,
  activeTab,
  onTabChange,
}) {
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");
  const { data: settingsPage } = useFeatureFlagsByCategory("settingsPage");

  const navItems = [
    {
      id: "overview",
      label: "Overview",
      icon: overviewIcon,
      svg: true,
      to: "/trading/business-suite",
      external: false,
    },
    {
      id: "dpos",
      label: "Digital POS",
      icon: null,
      svg: false,
      IconComponent: QrCode,
      to: "/trading/business-suite",
      external: false,
    },
    mainAccount && {
      id: "market",
      label: "Market",
      icon: marketIcon,
      svg: true,
      to: "/trading/business-suite",
      external: false,
    },
    (featureFlags?.buyCrypto || featureFlags?.sellCrypto) && {
      id: "buy-sell",
      label: "Buy/Sell",
      icon: buySellIcon,
      svg: true,
      to: "/trading/business-suite",
      navChildTab: "Buy",
      external: false,
    },
    mainAccount &&
      (featureFlags?.sendCrypto || featureFlags?.receiveCrypto) && {
        id: "send-receive",
        label: "Send/Receive",
        icon: sendIcon,
        svg: true,
        to: "/trading/business-suite",
        navChildTab: "send",
        external: false,
      },
    mainAccount &&
      (featureFlags?.deposit || featureFlags?.withdraw) && {
        id: "withdraw-deposit",
        label: "Withdraw/Deposit",
        icon: withdrawDepositIcon,
        svg: true,
        to: "/trading/business-suite",
        navChildTab: "deposit",
        external: false,
      },
    mainAccount &&
      featureFlags?.eightPay && {
        id: "80eightpay",
        label: "88Pay",
        icon: eightyEightPayIcon,
        svg: true,
        to: "/trading/business-suite",
        external: false,
      },
    mainAccount &&
      featureFlags?.eightPay && {
        id: "pay-link",
        label: "Pay Link",
        icon: payLinkIcon,
        svg: true,
        to: "/trading/business-suite",
        external: false,
      },
    featureFlags?.autostack && {
      id: "autostack",
      label: "AutoStack",
      icon: "zap",
      svg: false,
      to: "/autostack",
      external: true,
    },
    mainAccount &&
      settingsPage?.addressBook && {
        id: "address-book",
        label: "Address book",
        icon: accountIcon,
        svg: true,
        to: "/trading/business-suite",
        external: false,
      },
  ].filter(Boolean);

  return (
    <nav className="flex lg:flex-col flex-wrap pb-3 lg:space-y-3 lg:justify-start justify-center lg:items-start items-center">
      {navItems.map((item) => {
        const isActive = item.external ? false : activeTab === item.id;
        const baseClass = `relative flex items-center gap-[12px] w-full rounded-full py-[16px] px-[24px] border border-[1px] ${
          isActive
            ? "border-[#6D6584] text-[#33125F] font-semibold"
            : "border-transparent text-[#6D6584] font-normal"
        }`;

        if (!item.external) {
          return (
            <button
              key={item.id}
              type="button"
              onClick={() =>
                item.navChildTab !== undefined
                  ? onTabChange?.(item.id, { childTab: item.navChildTab })
                  : onTabChange?.(item.id)
              }
              className={baseClass}
            >
              <NavItemContent item={item} isActive={isActive} />
            </button>
          );
        }

        return (
          <Link key={item.id} to={item.to} className={baseClass}>
            <NavItemContent item={item} isActive={isActive} />
          </Link>
        );
      })}
    </nav>
  );
}
